/** Permission keys in `resource:action` form. */
export const PERMISSIONS = {
  'user:manage': 'Create, edit, invite and deactivate users',
  'role:manage': 'Create and edit roles and their permissions',
  'product:view': 'View products and services',
  'product:edit': 'Create and edit products, modules and releases',
  'client:view': 'View client organizations and subscriptions',
  'client:manage': 'Create and edit client organizations and subscriptions',
  'ticket:create': 'Raise tickets',
  'ticket:view_all': 'View all tickets within product scope (staff)',
  'ticket:view_own_org': "View all of own organization's tickets (client admin)",
  'ticket:assign': 'Assign tickets, set due dates and target releases',
  'ticket:update_status': 'Change ticket status',
  'ticket:comment_internal': 'Read and add internal notes',
  'work:view': 'View development work items',
  'work:manage': 'Create and edit any development work item',
  'work:update_assigned': 'Update work items assigned to me',
  'report:view': 'View dashboards and reports',
  'audit:view': 'View the audit log',
} as const;

export type PermissionKey = keyof typeof PERMISSIONS;
export const PERMISSION_KEYS = Object.keys(PERMISSIONS) as PermissionKey[];

export interface DefaultRole {
  name: string;
  description: string;
  scope: 'staff' | 'client';
  permissions: PermissionKey[];
}

/** Seeded system roles matching the default role matrix. */
export const DEFAULT_ROLES: DefaultRole[] = [
  {
    name: 'Super admin',
    description: 'Full access to configuration, users, roles and all data',
    scope: 'staff',
    permissions: [...PERMISSION_KEYS],
  },
  {
    name: 'Manager',
    description: 'Triage and assign tickets, track work for assigned products',
    scope: 'staff',
    permissions: [
      'user:manage',
      'product:view',
      'product:edit',
      'client:view',
      'ticket:create',
      'ticket:view_all',
      'ticket:assign',
      'ticket:update_status',
      'ticket:comment_internal',
      'work:view',
      'work:manage',
      'report:view',
    ],
  },
  {
    name: 'Employee',
    description: 'Resolve assigned tickets and work items',
    scope: 'staff',
    permissions: [
      'product:view',
      'ticket:create',
      'ticket:update_status',
      'ticket:comment_internal',
      'work:view',
      'work:update_assigned',
      'report:view',
    ],
  },
  {
    name: 'Client admin',
    description: "Manage own company's users and see all company tickets",
    scope: 'client',
    permissions: ['user:manage', 'client:view', 'ticket:create', 'ticket:view_own_org', 'report:view'],
  },
  {
    name: 'Client user',
    description: 'Raise and follow own tickets',
    scope: 'client',
    permissions: ['ticket:create'],
  },
];
